import { AxiosPromise, CancelTokenSource, AxiosRequestConfig } from 'axios';

import { HttpClient } from '@/shared/HttpClient';
import { Result } from '@/shared/models/Result.model';
import { FileContentResult } from '@/shared/models/FileContentResult.model';
import { TypeTemplate } from '@/shared/enums/TypeTemplate.enum';

/**
 * Service générique exposant les méthodes de bases d'appel à l'API.
 */
export class ApiService<T>  {
    // Endpoint d'API sur lequel requêter.
    protected endpoint: string;

    // Token permettant l'annulation des requête en cours si on en a une nouvelle.
    private cancelTokenSource: CancelTokenSource;

    /**
     * Création de la config de requête axios pour annuler les requêtes en cours quand on en a déjà.
     * */
    protected setupAnnulationEtGetConfig(): AxiosRequestConfig {
        // Annulation de la requête précédente si il y en avait une.
        if (this.cancelTokenSource && this.cancelTokenSource.token.reason === undefined) {
            this.cancelTokenSource.cancel('Annulation de la requête à cause d\'une nouvelle requête.');
        }

        // Génération du token d'annulation.
        this.cancelTokenSource = HttpClient.cancelToken.source();

        return { cancelToken: this.cancelTokenSource.token };
    }

    /**
     * Constructeur.
     * @param endpoint nom du endpoint de l'API.
     */
    constructor(endpoint: string) {
        this.endpoint = endpoint;
    }

    /**
     * Récupère toutes les entités de type T.
     * @returns Réponse axios ActionResult contenant toutes les entités de type T.
     */
    public getAll(): AxiosPromise<Result<T[]>> {
        return HttpClient.axios
            .get<Result<T[]>>(this.endpoint, this.setupAnnulationEtGetConfig());
    }

    /**
     * Récupère l'entité de type T d'identifiant "id".
     * @param id Identifiant de l'entité.
     * @returns Réponse axios ActionResult contenant l'entité T d'identifiant "id".
     */
    public get(id: number): AxiosPromise<Result<T>> {
        return HttpClient.axios.get<Result<T>>(`${this.endpoint}/${id}`, this.setupAnnulationEtGetConfig());
    }

    /**
     * Récupère l'entité de type T sans identifiant nécessaire.
     * @returns Réponse axios ActionResult contenant l'entité T.
     */
    public getSingle(): AxiosPromise<Result<T>> {
        return HttpClient.axios.get<Result<T>>(`${this.endpoint}`, this.setupAnnulationEtGetConfig());
    }


    /**
     * Récupère la dernière entité de type T.
     * @returns Réponse axios ActionResult contenant l'entité T.
     */
    public getLast(typeTemplate: TypeTemplate): AxiosPromise<Result<T>> {
        return HttpClient.axios.get<Result<T>>(`${this.endpoint}/last?CodeTypeTemplate=${typeTemplate}`, this.setupAnnulationEtGetConfig());
    }
    
    /**
     * Récupère un fichier à partir de l'identifiant "id".
     * @param id Identifiant de l'entité.
     * @returns Réponse axios FileContentResult contenant le fichier.
     */
    public getFile(id: number): AxiosPromise<FileContentResult> {
        return HttpClient.axios.get<FileContentResult>(`${this.endpoint}/${id}`, this.setupAnnulationEtGetConfig());
    }

    /**
     * Récupère une liste d'entité de type T selon des critères de recherche.
     * @param queryString Les critères de recherche sous forme de query string.
     * @returns Réponse axios ActionResult contenant la liste d'entité de type T.
     */
    public getWhere(queryString: string): AxiosPromise<Result<T[]>> {
        return HttpClient.axios.get<Result<T[]>>(`${this.endpoint}?${queryString}`, this.setupAnnulationEtGetConfig());
    }

    /**
     * Récupère une entité selon les critères de recherche.
     * @param queryString Les critères de recherche sous forme de query string.
     * @returns Réponse axios contenant l'entité.
     */
    public getWhereSingle(queryString: string): AxiosPromise<T> {
        return HttpClient.axios.get<T>(`${this.endpoint}?${queryString}`, this.setupAnnulationEtGetConfig());
    }

    /**
     * Crée une entité en base.
     * @param data L'entité.
     * @returns Réponse axios contenant l'entité T
     */
    public post(data: T): AxiosPromise<Result<T>> {
        return HttpClient.axios.post<Result<T>>(this.endpoint, data, this.setupAnnulationEtGetConfig());
    }

    /**
     * Modifie une entité en base.
     * @param data L'entité.
     * @returns Réponse axios contenant l'entité T
     */
    public put(data: T): AxiosPromise<Result<T>> {
        return HttpClient.axios.put<Result<T>>(this.endpoint, data, this.setupAnnulationEtGetConfig());
    }

    /**
     * Modifie une entité en base.
     * @param data L'entité.
     * @returns Réponse axios contenant les modifications partielles à mettre.
     */
    public patch(data: any): AxiosPromise<Result<boolean>> {
        return HttpClient.axios.patch<Result<boolean>>(this.endpoint, data, this.setupAnnulationEtGetConfig());
    }

    /**
     * Supprime l'entité de type T d'identifiant "id".
     * @param id Identifiant de l'entité.
     */
    public delete(id: number): AxiosPromise {
        return HttpClient.axios.delete(`${this.endpoint}/${id}`, this.setupAnnulationEtGetConfig());
    }
}
